import React, { useCallback } from 'react'

import type { CloudJob, CloudJobList, JobStatusEnum } from '@pollination-solutions/pollination-sdk'

import { APIClient } from './useAPIClient'

import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'

dayjs.extend(duration)
dayjs.extend(utc)


export const useJobs = (client: APIClient) => {
  const statusMap = { 
    pending: {
      color: '#FADB15'
    }, 
    running: {
      color: '#1890ff'
    }, 
    failed: {
      color: '#FF4616'
    }, 
    canceled: {
      color: 'var(--slate10)'
    }, 
    completed: {
      color: '#46A758'
    },
  }

  const getDuration = (item?: CloudJob) => {
    if (!item) return dayjs.duration({ seconds: 0 })
    const { status } = item
    
    if (status.finished_at) {
      return dayjs.duration(dayjs(status.finished_at)
        .diff(dayjs(status.started_at))) 
    }
  
    return dayjs.duration(dayjs().utc()
      .diff(dayjs(status.started_at).utc(true)))
  }

  const listJobs = useCallback((
    projectOwner: string, 
    projectName: string,
    jobIds?: string[],
    page?: number,
    perPage?: number,
    status?: JobStatusEnum,
  ): Promise<CloudJobList> => {
    
    if (!client || !client.jobs) return
    
    return client.jobs
      .listJobs({ 
        owner: projectOwner, 
        name: projectName ,
        ids: jobIds,
        status,
        page: page ?? 1,
        perPage: perPage ?? 10,
      })
      .then(({ data }) => data)

  }, [client])
  
  const fetchJob = useCallback((
    projectOwner: string, 
    projectName: string, 
    jobId: string,  
  ): Promise<CloudJob> => {
    
    if (!client || !client.jobs) return

    return client.jobs
      .getJob({ 
        owner: projectOwner, 
        name: projectName, 
        jobId 
      })
      .then(({ data }) => data)

  },[client])

  const deleteJob = useCallback((
    projectOwner: string, 
    projectName: string, 
    jobId: string
  ) => {
    
    if (!client || !client.jobs) return

    return client.jobs
      .deleteJob({ 
        owner: projectOwner, 
        name: projectName, 
        jobId 
      })
      .then(({ data }) => data)
      
  }, [client])

  const cancelJob = useCallback((
    projectOwner: string, 
    projectName: string, 
    jobId: string
  ) => {
    
    if (!client || !client.jobs) return
    
    return client.jobs
      .cancelJob({ 
        owner: projectOwner, 
        name: projectName, 
        jobId 
      })
      .then(({ data }) => data)

  }, [client])

  const retryFailedRuns = useCallback((
    projectOwner: string, 
    projectName: string, 
    jobId: string
  ) => {
    
    if (!client || !client.jobs) return
    
    return client.jobs
      .retryJob({ 
        owner: projectOwner, 
        name: projectName, 
        jobId,
        retryConfig: {
          hard: true
        }
      })
      .then(({ data }) => data)

  }, [client])

  return {
    listJobs,
    fetchJob,
    deleteJob,
    cancelJob,
    retryFailedRuns,
    statusMap,
    getDuration,
  }
}